import { HaftpflichtSchadenWithAufstellung } from '@gsm/entities';
import { DYNAMIC_FORM_CONTROL_TYPE_GROUP, DYNAMIC_FORM_CONTROL_TYPE_SELECT, MATCH_VISIBLE } from '@ng-dynamic-forms/core';
import { MATCH_COMPARE_WITH_HIDDEN } from '../../matchers/compare-with-visible.matcher';
import { TYPE_CUSTOM_FILE_UPLOAD } from '../custom-file-upload/custom-file-upload.model';
import { TYPE_DOKUMENTEN_ANGABE } from '../dokumenten-angabe/dokumenten-angabe.model';
import { TYPE_HAFTPFLICHT_FORDERUNG_AUFSTELLUNG } from '../haftpflicht-forderung-aufstellung/haftpflicht-forderung-aufstellung.model';
import { TYPE_HAFTPFLICHT_GESCHAEDIGTE_PERSON } from '../haftpflicht-geschaedigte-person/haftpflicht-geschaedigte-person.model';
import { TYPE_INFO } from '../info/info.model';
import { AnyFormControlModel } from '../shared-models/any-form-control.model';
import { AufstellungVorhanden, HaftpflichtSchadenVorhanden } from '../shared-models/haftpflichtschaden.model';
import { TYPE_ZAHLUNGSEMPFAENGER } from '../zahlungsempfaenger/zahlungsempfaenger.model';
import { HaftpflichtPersonenschadenAdditional } from './haftpflicht-personenschaden.model';

interface HaftpflichtPersonenschadenLabels {
  aufstellung: string;
  aufstellungVorhandenInfo: string;
  aufstellungVorhandenLabel: string;
  aufstellungVorhanden: string;
  aufstellungNachgebracht: string;
  aufstellungErstellen: string;
  zahlungsempfaengerInfo: string;
  belege: string;
}

const GESCH_PERSONEN_LABELS: HaftpflichtPersonenschadenLabels = {
  aufstellung:               'Forderung der geschädigten Person',
  aufstellungVorhandenInfo:  'Ist eine Forderung vorhanden?',
  aufstellungVorhandenLabel: 'Forderung',
  aufstellungVorhanden:      'Ja, es ist eine Forderung vorhanden.',
  aufstellungNachgebracht:   'Ja, eine Forderung wird nachgebracht.',
  aufstellungErstellen:      'Nein, Forderung erstellen.',
  zahlungsempfaengerInfo:    'Bitte wählen Sie den Zahlungsempfänger der Forderung aus.',
  belege:                    'Belege zur Forderung'
};

const VERMOEGENSSCHADEN_LABELS: HaftpflichtPersonenschadenLabels = {
  aufstellung:               'Vermögensschaden',
  aufstellungVorhandenInfo:  'Ist eine Vermögensschadenaufstellung vorhanden?',
  aufstellungVorhandenLabel: 'Vermögensschadenaufstellung',
  aufstellungVorhanden:      'Ja, es ist eine Vermögensschadenaufstellung vorhanden.',
  aufstellungNachgebracht:   'Ja, eine Vermögensschadenaufstellung wird nachgebracht.',
  aufstellungErstellen:      'Nein, Vermögensschadenaufstellung erstellen.',
  zahlungsempfaengerInfo:    'Bitte wählen Sie den Zahlungsempfänger der Vermögensschadenaufstellung aus.',
  belege:                    'Belege zum Vermögensschaden'
};

export function HAFTPFLICHT_PERSONENSCHADEN_TEMPLATE(
  value: HaftpflichtSchadenWithAufstellung,
  modelValue,
  {
    docTypBelege,
    geschPersList,
    kundeList,
    updateClaim,
    paperProcessOverride,
    vermoegensschaden
  }: HaftpflichtPersonenschadenAdditional = {},
  isEditEvent = false
): AnyFormControlModel[] {
  const labels = vermoegensschaden ? VERMOEGENSSCHADEN_LABELS : GESCH_PERSONEN_LABELS;

  const {
    id,
    vorhanden,
    geschaedigtePerson,
    aufstellungVorhanden,
    aufstellung: {
                   checklistItemFileList: aufstellungFileList,
                   zahlungsempfaenger:    aufstellungZahlungsempfaenger,
                   generationData:        {forderungAufstellung} = {} as any
                 } = {} as any,
    belege
  } = value || {};

  if (!isEditEvent && geschPersList && modelValue.length) {
    // only persons who are not in the local modelValue
    geschPersList = geschPersList.filter(
      geschPers => !modelValue.some(val => val.geschaedigtePerson?.correlationId === geschPers.correlationId)
    );
  }

  return [
    {
      id:         'geschaedigtePerson',
      type:       TYPE_HAFTPFLICHT_GESCHAEDIGTE_PERSON,
      value:      {
        id,
        vorhanden,
        geschaedigtePerson
      },
      additional: {
        geschPersList,
        updateClaim,
        paperProcessOverride,
        detailsNachbringen: aufstellungVorhanden === AufstellungVorhanden.Nachgebracht
      }
    },
    {
      id:        'aufstellung_group',
      type:      DYNAMIC_FORM_CONTROL_TYPE_GROUP,
      label:     labels.aufstellung,
      hidden:    true,
      layout:    {
        element: {
          host: 'gsm-section-headline-3'
        }
      },
      relations: [
        {
          match: MATCH_COMPARE_WITH_HIDDEN,
          when:  [
            {
              id:          'geschaedigtePerson',
              compareWith: val => !val?.vorhanden || val.vorhanden === HaftpflichtSchadenVorhanden.Nachgebracht
            }
          ]
        }
      ],
      group:     [
        {
          id:            'aufstellungVorhanden_instruction',
          type:          TYPE_INFO,
          content:       labels.aufstellungVorhandenInfo,
          isInstruction: true
        },
        {
          id:       'aufstellungVorhanden',
          type:     DYNAMIC_FORM_CONTROL_TYPE_SELECT,
          label:    labels.aufstellungVorhandenLabel,
          required: true,
          options:  [
            {
              label: labels.aufstellungVorhanden,
              value: AufstellungVorhanden.Vorhanden
            },
            {
              label: labels.aufstellungNachgebracht,
              value: AufstellungVorhanden.Nachgebracht
            },
            {
              label: labels.aufstellungErstellen,
              value: AufstellungVorhanden.Erfassen
            }
          ],
          value:    aufstellungVorhanden
        },
        {
          id:           'aufstellungFileList',
          type:         TYPE_CUSTOM_FILE_UPLOAD,
          labelTooltip: 'Dokument hinzufügen',
          required:     true,
          hidden:       true,
          relations:    [
            {
              match: MATCH_VISIBLE,
              when:  [
                {
                  id:    'aufstellungVorhanden',
                  value: AufstellungVorhanden.Vorhanden
                }
              ]
            }
          ],
          value:        aufstellungFileList
        },
        {
          id:        'forderungAufstellung',
          type:      TYPE_HAFTPFLICHT_FORDERUNG_AUFSTELLUNG,
          required:  true,
          hidden:    true,
          relations: [
            {
              match: MATCH_VISIBLE,
              when:  [
                {
                  id:    'aufstellungVorhanden',
                  value: AufstellungVorhanden.Erfassen
                }
              ]
            }
          ],
          value:     forderungAufstellung
        },
        {
          id:        'aufstellungZahlungsempfaenger_group',
          type:      DYNAMIC_FORM_CONTROL_TYPE_GROUP,
          hidden:    true,
          relations: [
            {
              match: MATCH_VISIBLE,
              when:  [
                {
                  id:    'aufstellungVorhanden',
                  value: AufstellungVorhanden.Vorhanden
                },
                {
                  id:    'aufstellungVorhanden',
                  value: AufstellungVorhanden.Erfassen
                }
              ]
            }
          ],
          group:     [
            {
              id:            'aufstellungZahlungsempfaenger_instruction',
              type:          TYPE_INFO,
              content:       labels.zahlungsempfaengerInfo,
              isInstruction: true
            },
            {
              id:                     'aufstellungZahlungsempfaenger',
              type:                   TYPE_ZAHLUNGSEMPFAENGER,
              required:               true,
              kunden:                 kundeList,
              removeRechnungssteller: true,
              addDirektAnGeschPerson: true,
              additional:             {
                immediateValidation: true
              },
              value:                  aufstellungZahlungsempfaenger
            }
          ]
        },
        {
          id:        'aufstellungBelege_group',
          type:      DYNAMIC_FORM_CONTROL_TYPE_GROUP,
          label:     labels.belege,
          hidden:    true,
          relations: [
            {
              match: MATCH_VISIBLE,
              when:  [
                {
                  id:    'aufstellungVorhanden',
                  value: AufstellungVorhanden.Vorhanden
                },
                {
                  id:    'aufstellungVorhanden',
                  value: AufstellungVorhanden.Erfassen
                }
              ]
            }
          ],
          group:     [
            {
              id:         'aufstellungBelege',
              type:       TYPE_DOKUMENTEN_ANGABE,
              additional: {
                optionalTypes:       docTypBelege,
                kunden:              kundeList,
                immediateValidation: true
              },
              value:      belege
            }
          ]
        }
      ]
    }
  ];
}
